import * as CGTypes from './types';
import * as Types from './model/types';
import * as HL from './hl';
import {constNumber} from './consts';
import * as Op from './ops';
import {opLimit, opFlatten, opIndex} from './ops.primitives';

/**
 * We may have a `runs.index(var('x'))` in the executableNode graph.
 * For example, PanelTable column select expressions always contain
 * `index(var('x'))` during editing, to indicate that the expression
 * will be applied to all rows in the table's underlying array.
 * Here we replace `runs.index(var('x'))` with `runs.flatten().limit(10)`. Which
 * means the resulting type will be a union of the summary types
 * for the first 10 runs in the array.
 *
 * We may just want to do this in HL.refineNode all the time. But we can't
 * import ops into HL, that would create a cycle. We'll need a bigger refactor
 */
export const replaceInputVariables = (runNode: Types.Node) => {
  return HL.mapNodes(
    runNode,

    checkNode => {
      if (
        checkNode.nodeType === 'output' &&
        // Look for index ops
        checkNode.fromOp.name === 'index' &&
        // Using a variable for the index argument
        checkNode.fromOp.inputs.index.nodeType === 'var'
      ) {
        return opLimit({
          arr: opFlatten({arr: checkNode.fromOp.inputs.arr as any}),
          limit: constNumber(10),
        });
      }
      return checkNode;
    }
  ) as Types.Node;
};

/**
 * Given a FunctionType, return a frame representing only the typed variables
 * that should be available inside the function. For the function body,
 * this should be merged with the frame of the parent context.
 *
 * opIndex would cause a circular import in hl.ts -- without it this could go there
 */
export function getFunctionFrame(
  opName: string,
  firstArg: CGTypes.EditingNode,
  existingFrame: {[varName: string]: Types.Node}
): {[varName: string]: Types.Node} {
  // TODO: commented out below is what we _want_ to be able to do here. What holds us back
  // is that right now, all supported ops with function args take functions whose
  // type is dependent on the type of the first argument to the op. Easier by example:
  //
  // rows.filter((row) => row['name'] == 'Name')
  //
  // in this case, the function needs to take a row typed as a dictionary containing
  // a string property called 'name'. To do this, we need something like type parameters.
  // e.g. in TS filter would be typed:
  //
  // function filter<T>(arr: T[], callback: (item: T) => boolean) {}
  //
  // So the type of the second argument (callback) depends on the type of the first
  // (arr). We have no mechanism for doing this yet, so the code below doesn't work
  // unless you happen to be dealing with an op whose callback args ALWAYS take
  // the same argument types.

  // where functionType would be something pulled off of, e.g., opDef.inputTypes:

  // return mapValues(functionType.inputTypes, (inputType, argName) =>
  //   Graph.varNode(inputType, argName)
  // );

  // to get around this, we're implementing the type resolution behavior here for the
  // first few function-based op types we want to support. This is kind of like the
  // first pass on what would become `renderInfo`: eventually we will make this
  // behavior something we define on op definitions themselves. It's more dynamic
  // than render types, though, so it's gonna take some thought.

  // let frame: Code.Frame = {};
  let innerFrame: {[varName: string]: Types.Node} = {};
  firstArg = HL.dereferenceVariables(
    firstArg as Types.Node,
    existingFrame
  ).node;
  if (
    opName === 'groupby' ||
    opName === 'filter' ||
    opName === 'sort' ||
    opName === 'join' ||
    opName === 'map'
  ) {
    innerFrame = {
      row: opIndex({
        arr: Op.opDropNa({arr: firstArg as any}),
        // This technically should be a var node called index of type number,
        // however, calls to refinedOutputType try to execute these frames
        // resulting in invalid executions.
        index: constNumber(0),
      }),
    };
  } else if (opName === 'joinAll') {
    // TODO: this probably won't work for editing joinAll in the UI; putting
    // it in just so that this op doesn't throw an error when we hit it in
    // refineEditingNode
    innerFrame = {};
  } else if (opName === 'number-bin') {
    innerFrame = {
      row: firstArg as any,
    };
  } else {
    throw new Error('Invalid: function editor for unknown op: ' + opName);
  }
  return {
    ...existingFrame,
    ...innerFrame,
  };
}

/**
 * Get the frame (the available variables) at `targetNodeOrOp`. This will be the
 * frame provided to the root of the expression, plus any frames added by function
 * literals in which the node was nested. e.g. for node _ in
 *
 * arr.filter(row => _)
 *
 * the frame would be {arr, row} (assuming that arr was provided at the root). For
 * the tail node of the same expression, the frame would only include {arr}.
 *
 * Returns null if the node is not found in `graph`.
 */
export function getFrameAtNodeOrOp(
  graph: CGTypes.EditingNode,
  targetNodeOrOp: CGTypes.EditingNode | CGTypes.EditingOp,
  parentFrame: {[name: string]: Types.Node}
): {[varName: string]: Types.Node} | null {
  if (graph === targetNodeOrOp) {
    return parentFrame;
  }

  if (HL.isFunctionLiteral(graph)) {
    return getFrameAtNodeOrOp(graph.val, targetNodeOrOp, parentFrame);
  }

  if (graph.nodeType === 'output') {
    //  are we targeting this output's Op?
    if (graph.fromOp === targetNodeOrOp) {
      // if so, we have the frame we want:
      return parentFrame;
    }

    const inputNames = Object.keys(graph.fromOp.inputs);
    // recurse into each of the inputs of this operation
    for (const inputName of inputNames) {
      const input = graph.fromOp.inputs[inputName];

      if (HL.isFunctionLiteral(input)) {
        const functionBodyFrame = getFunctionFrame(
          graph.fromOp.name,
          graph.fromOp.inputs[inputNames[0]],
          parentFrame
        );
        const foundFrameInFunctionBody = getFrameAtNodeOrOp(
          input,
          targetNodeOrOp,
          functionBodyFrame
        );

        if (foundFrameInFunctionBody !== null) {
          return foundFrameInFunctionBody;
        }

        continue;
      }

      const foundFrame = getFrameAtNodeOrOp(input, targetNodeOrOp, parentFrame);

      if (foundFrame !== null) {
        return foundFrame;
      }
    }
  }

  return null;
}
